import { useState } from 'react'
import {Calendar, Clock, User, Mail, Phone, Car, Wrench, FileText} from 'lucide-react'

interface AppointmentFormData {
  customer_name: string
  customer_email: string
  customer_phone: string
  vehicle_year: string
  vehicle_make: string
  vehicle_model: string
  service_requested: string
  appointment_date: string
  appointment_time: string
  notes: string
}

const SERVICES = [
  'Paint Protection Film (PPF)',
  'Full Vehicle Wrap',
  'Partial Vehicle Wrap',
  'Chrome Delete',
  'Window Tinting',
  'Caliper Painting',
  'Headlight/Taillight Tint',
  'Interior Wrap & Trim',
  'Racing Stripes & Graphics',
  'Commercial Fleet Wraps',
  'Other (specify in notes)'
]

const TIME_SLOTS = [
  '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
  '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM',
  '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM', '5:00 PM'
]

const SATURDAY_TIME_SLOTS = [
  '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
  '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM', '3:00 PM'
]

export function AppointmentBookingForm() {
  const [formData, setFormData] = useState<AppointmentFormData>({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    vehicle_year: '',
    vehicle_make: '',
    vehicle_model: '',
    service_requested: '',
    appointment_date: '',
    appointment_time: '',
    notes: ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const getAvailableTimeSlots = () => {
    if (!formData.appointment_date) return TIME_SLOTS
    
    const selectedDate = new Date(formData.appointment_date)
    const dayOfWeek = selectedDate.getDay()
    
    // Saturday (6) - limited hours
    if (dayOfWeek === 6) {
      return SATURDAY_TIME_SLOTS
    }
    
    // Monday-Friday (1-5) - full hours
    return TIME_SLOTS
  }

  const isDateDisabled = (dateString: string) => {
    const date = new Date(dateString)
    const dayOfWeek = date.getDay()
    
    // Disable Sundays (0)
    return dayOfWeek === 0
  }

  const getMinDate = () => {
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    return tomorrow.toISOString().split('T')[0]
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      // Validate date is not Sunday
      if (formData.appointment_date) {
        const selectedDate = new Date(formData.appointment_date)
        if (selectedDate.getDay() === 0) {
          setError('We are closed on Sundays. Please select a different date.')
          setLoading(false)
          return
        }
      }

      const response = await fetch('https://api.lumi.new/v1/functions/p387742853470646272/appointment-submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.message || 'Failed to book appointment')
      }

      setSuccess(true)
      setFormData({
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        vehicle_year: '',
        vehicle_make: '',
        vehicle_model: '',
        service_requested: '',
        appointment_date: '',
        appointment_time: '',
        notes: ''
      })

      // Reset success message after 5 seconds
      setTimeout(() => setSuccess(false), 5000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))

    // Reset time if date changes
    if (name === 'appointment_date') {
      setFormData(prev => ({ ...prev, appointment_time: '' }))
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-8">
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Book Your Appointment</h2>
        <p className="text-gray-600">
          Schedule a time that works for you. We'll send you a confirmation via email and text.
        </p>
      </div>

      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-800 font-medium">
            ✓ Appointment booked successfully! You'll receive a confirmation email and text message shortly.
          </p>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Personal Information */}
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
              <User className="w-4 h-4 mr-2" />
              Full Name *
            </label>
            <input
              type="text"
              name="customer_name"
              value={formData.customer_name}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="John Doe"
            />
          </div>

          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
              <Mail className="w-4 h-4 mr-2" />
              Email Address *
            </label>
            <input
              type="email"
              name="customer_email"
              value={formData.customer_email}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="john@example.com"
            />
          </div>

          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
              <Phone className="w-4 h-4 mr-2" />
              Phone Number *
            </label>
            <input
              type="tel"
              name="customer_phone"
              value={formData.customer_phone}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="(858) 282-8119"
            />
          </div>
        </div>

        {/* Vehicle Information */}
        <div className="grid md:grid-cols-3 gap-6">
          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
              <Car className="w-4 h-4 mr-2" />
              Vehicle Year *
            </label>
            <input
              type="text"
              name="vehicle_year"
              value={formData.vehicle_year}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="2023"
            />
          </div>

          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
              <Car className="w-4 h-4 mr-2" />
              Make *
            </label>
            <input
              type="text"
              name="vehicle_make"
              value={formData.vehicle_make}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Tesla"
            />
          </div>

          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
              <Car className="w-4 h-4 mr-2" />
              Model *
            </label>
            <input
              type="text"
              name="vehicle_model"
              value={formData.vehicle_model}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Model 3"
            />
          </div>
        </div>

        {/* Service Selection */}
        <div>
          <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
            <Wrench className="w-4 h-4 mr-2" />
            Service Requested *
          </label>
          <select
            name="service_requested"
            value={formData.service_requested}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Select a service...</option>
            {SERVICES.map(service => (
              <option key={service} value={service}>{service}</option>
            ))}
          </select>
        </div>

        {/* Date and Time Selection */}
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
              <Calendar className="w-4 h-4 mr-2" />
              Preferred Date *
            </label>
            <input
              type="date"
              name="appointment_date"
              value={formData.appointment_date}
              onChange={handleChange}
              min={getMinDate()}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="mt-1 text-sm text-gray-500">
              Mon-Fri: 9 AM - 5 PM | Sat: 9 AM - 3 PM | Sun: Closed
            </p>
          </div>

          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
              <Clock className="w-4 h-4 mr-2" />
              Preferred Time *
            </label>
            <select
              name="appointment_time"
              value={formData.appointment_time}
              onChange={handleChange}
              required
              disabled={!formData.appointment_date}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">Select a time...</option>
              {getAvailableTimeSlots().map(time => (
                <option key={time} value={time}>{time}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Additional Notes */}
        <div>
          <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
            <FileText className="w-4 h-4 mr-2" />
            Additional Notes
          </label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={4}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Any specific requests or questions?"
          />
        </div>

        {/* Special Hours Note */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Need a time outside our normal hours?</strong> Contact us directly at{' '}
            <a href="tel:8582828119" className="underline font-medium">858-282-8119</a> and we may be able to accommodate special scheduling requests.
          </p>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold py-4 px-8 rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Booking...' : 'Book Appointment'}
        </button>
      </form>
    </div>
  )
}
